import { getLoginContext } from '@athena-lib/loginContext.svelte.js';
import type { CartPayment, CheckoutStateData, Order } from '@stickerapp-org/checkout-sdk';
import type { CheckoutErrors } from '@athena-lib/Checkout/Checkout.svelte';
import { objectEntries } from '@athena-lib/object-utils';
import { Type } from '@sinclair/typebox';
import type { CheckoutSdk } from '@athena-lib/Checkout/utils';
import { currentOriginalSlug } from '../routes/[...slug]/currentOriginalSlug.svelte';
import { get } from './http';
import { type CheckoutVersion, getCheckoutSdk } from '$lib/checkout.svelte';

type FormInteractionEvent = 'error' | 'shown' | 'started' | 'completed' | 'submitted';
type UserTypes = 'business' | 'private';

const formatError = <T extends keyof CheckoutErrors>(
  key: T,
  value: CheckoutErrors[T],
): Array<string> => {
  if (typeof value === 'object' && 'title' in value) {
    return [`${key}: ${value.description.length > 0 ? value.description : value.title}`];
  }
  if (typeof value === 'object') {
    return Object.entries(value).map(([deepKey, value]) => `${key}.${deepKey}: ${value}`);
  }
  return [`${key}: ${value}`];
};

const ProductItemSchema = Type.Object({
  gtm_item: Type.Object({
    item_id: Type.String(),
  }),
});

export const getCheckoutGtmStateMachine = () => {
  const submittedUniqueEvents: Array<FormInteractionEvent> = [];
  let userType: UserTypes = 'private';
  const submitUniqueEvent = (eventType: FormInteractionEvent) => {
    if (!submittedUniqueEvents.includes(eventType)) {
      triggerGtmFormInterActionEvent(eventType, userType);
      submittedUniqueEvents.push(eventType);
    }
  };

  submitUniqueEvent('shown');

  return {
    setUserType: (newUserType: UserTypes) => {
      userType = newUserType;
    },
    reportFormErrors: (checkoutErrors: CheckoutErrors) => {
      // we don't want to report already reported errors
      const currentErrors = objectEntries(checkoutErrors)
        .filter(([key, value]) => {
          return (
            (value !== undefined || (typeof value === 'object' && Object.keys(value).length > 0)) &&
            key !== 'global'
          );
        })
        .flatMap(([key, value]) => formatError(key, value));

      triggerGtmFormInterActionEvent('error', userType, {
        checkoutErrors: currentErrors,
      });
    },
    // any field changed
    startForm: () => submitUniqueEvent('started'),
    // the form is valid
    completeForm: () => {
      submitUniqueEvent('started');
      submitUniqueEvent('completed');
    },
    submitForm: () => {
      submitUniqueEvent('started');
      submitUniqueEvent('completed');
      submitUniqueEvent('submitted');
    },
  };
};

const getSdkProps = () => {
  const checkoutSdk = getCheckoutSdk();
  return {
    gift_cards: checkoutSdk?.state.giftCards.map((x) => x.code),
    discount_codes: checkoutSdk?.state.discountCodes.map((x) => x.code),
    shipping_method: checkoutSdk?.state.checkout?.shipping.selectedShippingMethod?.name,
    payment_method: formatPaymentMethod(checkoutSdk?.state.checkout?.payment),
  };
};

const formatPaymentMethod = (paymentMethod: CartPayment | undefined) => {
  switch (paymentMethod?.$type) {
    case undefined:
      return '';
    case 'alreadyPaid':
    case 'free':
    case 'stripeCheckout':
      return paymentMethod.$type;
    case 'checkoutPage': {
      return paymentMethod.selectedPaymentMethod?.name;
    }
  }
};

function triggerGtmFormInterActionEvent(
  interactionType: FormInteractionEvent,
  user_type: UserTypes,
  errorType?: unknown,
) {
  const signedIn = getLoginContext().isLoggedIn;
  const eventParams = {
    checkout_version: '2',
    checkout_user_type: user_type,
    signed_in: signedIn.toString(),
    ...(errorType ? { error_type: errorType } : {}),
  };

  document.dispatchEvent(
    new CustomEvent('gtm-event', {
      detail: {
        event: 'form_interaction',
        event_params: {
          event_action: `checkout_form_${interactionType}`,
          event_group: 'checkout_form',
          event_variant: 'standard_checkout',
          default_path: currentOriginalSlug.slug,
          ...getSdkProps(),
          ...eventParams,
        },
      },
    }),
  );
}

export type CartContext = 'cart_drawer' | 'cart_page' | 'cart_icon' | 'checkout_page';

export const getCartEventsTracker = (variant: CartContext) => {
  type ActionType =
    | 'cart_view_cart_drawer'
    | 'cart_view_cart_page'
    | 'cart_edit_item'
    | 'cart_duplicate_item'
    | 'cart_change_quantity'
    | 'cart_remove_item'
    | 'cart_go_to_checkout';

  const dispatchEvent = (
    actionType: ActionType,
    params?: { change_from: number; change_to: number },
  ) => {
    document.dispatchEvent(
      new CustomEvent('gtm-event', {
        detail: {
          event: 'cart_interaction',
          event_params: {
            event_action: actionType,
            event_group: 'cart',
            event_variant: variant,
            default_path: currentOriginalSlug.slug,
            ...params,
          },
        },
      }),
    );
  };
  return {
    viewMiniCart: () => dispatchEvent('cart_view_cart_drawer'),
    viewReviewCart: () => dispatchEvent('cart_view_cart_page'),
    onEdit: () => dispatchEvent('cart_edit_item'),
    onDuplicate: () => dispatchEvent('cart_duplicate_item'),
    onChangeQuantity: (params: { change_from: number; change_to: number }) =>
      dispatchEvent('cart_change_quantity', params),
    onRemove: () => dispatchEvent('cart_remove_item'),
    goToCheckout: () => dispatchEvent('cart_go_to_checkout'),
  };
};

export const getEnhancedCommereceEventsTracker = (getCheckoutSdk: () => CheckoutSdk) => {
  const getCheckoutState = async (): Promise<CheckoutStateData> => {
    const checkoutSdk = getCheckoutSdk();
    if (!checkoutSdk.state.checkout) {
      await checkoutSdk.actions.loadCheckout();
    }
    return checkoutSdk.state;
  };

  type BasePropState = {
    lines: CheckoutStateData['lines'];
    currency: string;
  };

  const getBaseProps = async (state: BasePropState): Promise<BaseEventProps> => {
    const items = await Promise.all(state.lines.map((x, i) => toGtmEcommerceItem(x, i)));
    const filteredItems = items.filter((x) => x !== undefined) as EcommerceItem[];
    return {
      checkout_version: '2',
      currency: state.currency,
      value: filteredItems.map((x) => x.price * x.quantity).reduce((a, b) => a + b, 0),
      items: filteredItems,
    };
  };

  const createEventConstructor =
    (eventName: EnhancedCommerceEventType) =>
    async ({
      checkoutData,
      eventAction = eventName,
    }:
      | {
          checkoutData?: BasePropState;
          eventAction?: string;
        }
      | undefined = {}) =>
      document.dispatchEvent(
        new CustomEvent('gtm-event', {
          detail: {
            event: eventName,
            ecommerce: {
              ...(await getBaseProps(checkoutData ?? (await getCheckoutState()))),
            } satisfies EnhancedCommerceEvent,
            event_params: {
              ...(eventAction ? { event_action: eventAction } : {}),
              event_group: 'enhanced_ecommerce',
              default_path: currentOriginalSlug.slug,
            },
          },
        }),
      );

  const addToCart = createEventConstructor('add_to_cart');
  const removeFromCart = createEventConstructor('remove_from_cart');

  let latestLineItems: CheckoutStateData['lines'] | undefined = undefined;
  let currentCartId: string | undefined = undefined;

  return {
    viewCart: createEventConstructor('view_cart'),
    beginCheckout: createEventConstructor('begin_checkout'),
    reportUpdatedLineItems: ({
      lines: newLineItems,
      isLoading,
      id,
      currency,
    }: CheckoutStateData) => {
      // sets the initial value
      if (currentCartId !== id) {
        latestLineItems = newLineItems;
        currentCartId = id;
        return;
      }
      if (latestLineItems === undefined) {
        if (!isLoading) {
          latestLineItems = newLineItems;
          currentCartId = id;
        }
        return;
      }
      const oldLineItems = latestLineItems;

      // find the ones (by id AND quantity) that are different, they should generate removed and added events
      const addedLineItems = newLineItems.filter(
        (newItem) =>
          oldLineItems.findIndex(
            (oldItem) => oldItem.id === newItem.id && oldItem.quantity === newItem.quantity,
          ) === -1,
      );
      const removedLineItems = oldLineItems.filter(
        (oldItem) =>
          newLineItems.findIndex(
            (newItem) => oldItem.id === newItem.id && oldItem.quantity === newItem.quantity,
          ) === -1,
      );

      if (removedLineItems.length > 0) {
        removeFromCart({ checkoutData: { lines: removedLineItems, currency } });
      }

      if (addedLineItems.length > 0) {
        addToCart({ checkoutData: { lines: addedLineItems, currency } });
      }

      latestLineItems = newLineItems;
    },

    purchase: async (order: Order) => {
      const orderState = order;
      const shipping = orderState.shippingMethod.price.total.major.amountInclTax ?? 0;
      const tax = orderState.tax?.amountTotal?.major ?? 0;

      const basePrams = {
        type: 'purchase',
        transaction_id: '',
        shipping,
        tax,
        coupon: orderState.discountCodes?.at(0)?.code ?? '',
        ...(await getBaseProps({
          lines: orderState.lines,
          currency: orderState.currency,
        })),
      } satisfies EnhancedCommerceEvent;

      document.dispatchEvent(
        new CustomEvent('gtm-event', {
          detail: {
            event: 'purchase' satisfies EnhancedCommerceEventType,
            ecommerce: { ...basePrams, transaction_id: orderState.id },
            event_params: {
              event_action: 'purchase',
              event_group: 'enhanced_ecommerce',
              default_path: currentOriginalSlug.slug,
            },
          },
        }),
      );
    },
  };
};

type EnhancedCommerceEventType =
  | 'add_to_cart'
  | 'view_cart'
  | 'purchase'
  | 'remove_from_cart'
  | 'begin_checkout';

type BaseEventProps = {
  checkout_version: CheckoutVersion;
  currency: string;
  value: number;
  items: Array<EcommerceItem>;
};
type EnhancedCommerceEvent = (
  | {
      type: 'purchase';
      transaction_id: string;
      tax: number;
      shipping: number;
      coupon: string;
    }
  | object
) &
  BaseEventProps;

export const toGtmEcommerceItem = async (
  lineItem: CheckoutStateData['lines'][number],
  index: number,
): Promise<EcommerceItem | undefined> => {
  const price = lineItem.price.unit.major.amountInclTax;
  let id;
  let type;
  switch (lineItem.content.type) {
    case 'giftCard':
      id = lineItem.content.figureId;
      type = 'giftcard';
      break;
    case 'stickerPack':
      id = lineItem.content.stickerPackId;
      type = 'sticker-pack';
      break;
  }
  const { item_id } = await (lineItem.content.type === 'giftCard' ||
  lineItem.content.type === 'stickerPack'
    ? get(`/products/get_product/${type}?id=${id}`, ProductItemSchema).then((res) => ({
        item_id: res.gtm_item.item_id,
      }))
    : Promise.resolve({ item_id: '' }));
  switch (lineItem.content.type) {
    case 'giftCard': {
      return {
        index,
        price,
        item_id: item_id,
        item_category: 'library',
        item_category2: 'stickers',
        item_category3: 'singles',
        item_category4: '',
        item_category5: '',
        item_name: 'giftcard',
        product_id: lineItem.content.figureId,
        item_brand: 'StickerApp',
        item_variant: 'special',
        quantity: lineItem.quantity,
      };
    }
    case 'stickerPack': {
      return {
        index,
        price,
        item_id: item_id,
        item_category: 'library',
        item_category2: 'stickers',
        item_category3: 'singles',
        item_category4: '',
        item_category5: '',
        item_name: [lineItem.content.productName, lineItem.content.productFamilyName].join(' '),
        product_id: String(lineItem.content.stickerPackId),
        item_brand: 'StickerApp',
        item_variant: 'sticker_pack',
        quantity: lineItem.quantity,
      };
    }

    case 'customSticker': {
      const dimensions =
        !isNaN(Number(lineItem.content.attributes.width_mm)) &&
        !isNaN(Number(lineItem.content.attributes.height_mm))
          ? `${Math.round(Number(lineItem.content.attributes.width_mm) / 10)}x${Math.round(
              Number(lineItem.content.attributes.height_mm) / 10,
            )}`
          : '';

      const item_category = lineItem.content.name === 'library_design' ? 'library' : 'custom';
      return {
        price,
        item_brand: 'StickerApp',
        item_id: lineItem.content.legacy_sku ?? undefined,
        product_id: String(lineItem.content.customStickerId),
        item_variant: String(lineItem.content.attributes['material']),
        item_category,
        item_category2: 'stickers',
        item_category3: String(lineItem.content.attributes['laminate']),
        item_category4: dimensions,
        item_category5: String(lineItem.content.attributes['sheet_name']),
        ...(item_category === 'library'
          ? {
              item_list_id: 'sticker_shop',
              item_list_name: 'sticker_shop',
            }
          : {}),

        item_name: lineItem.content.productName,
        quantity: lineItem.quantity,
        index,
      } as const;
    }
  }
};

export type EcommerceItem = {
  item_id?: string;
  product_id: string;
  item_name: string;
  item_category: 'custom' | 'library';
  item_brand: 'StickerApp';
  item_category2: 'stickers';
  item_category3: string; // laminate
  item_category4: string;
  item_category5: string;
  item_variant: string;
  item_list_name?: 'sticker_shop';
  item_list_id?: 'sticker_shop';
  price: number;
  quantity: number;
  index: number;
};
